import { ChartOptions } from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useTranslate, useLocaleState } from "ra-core";

import useLongitudinal from "shared/hooks/useLongitudinal";
import { YYYYMMDDtoDate } from "shared/utils/date";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

// WMH regions to stack, in display order.
//  - label : legend name shown to the user
//  - code  : RID used to look up the value in each timepoint's quantification
//  - color : fixed bar color
// Note RID39467:RID45765 is keyed "Cortical" in the quantification JSON but is
// displayed as "Juxtacortical".
export const WMH_REGIONS: { code: string; label: string; color: string }[] = [
  {
    code: "RID39467:RID6384",
    label: "Periventricular",
    color: "rgb(181, 112, 255)",
  },
  { code: "RID39467:RID6387", label: "Deep", color: "rgb(2, 186, 189)" },
  {
    code: "RID39467:RID45765",
    label: "Juxtacortical",
    color: "rgb(254, 196, 0)",
  },
  {
    code: "RID39467:RID6381",
    label: "Infratentorial",
    color: "rgb(153, 255, 153)",
  },
];

// Backward-compat export (was previously a flat list of codes).
export const WMH_CODES = WMH_REGIONS.map((r) => r.code);

function LongitudinalGraph() {
  const { timepoints, isDone } = useLongitudinal();
  const [currentLocal] = useLocaleState();
  const t = useTranslate();
  const dateFormatter = new Intl.DateTimeFormat(currentLocal, {
    dateStyle: "long",
  });

  if (!timepoints?.length || !isDone) {
    return null;
  }

  function computeLongitudinalData() {
    const quantitativeData = timepoints.map((tp) => ({
      studyDate: dateFormatter.format(YYYYMMDDtoDate(tp.studyDate)),
      quantification: tp.quantification,
    }));

    const datasets = WMH_REGIONS.map(({ code, label, color }) => ({
      // translate if a key exists, otherwise fall back to the literal name
      label: t(label, { _: label }),
      data: quantitativeData.map(
        (q) =>
          q.quantification.find(
            (quant) => quant[0] === code && quant[2] === "cm3",
          )?.[1],
      ),
      backgroundColor: color,
      barThickness: 75,
    }));

    return {
      labels: quantitativeData.map((q) => q.studyDate),
      datasets,
    };
  }

  const data = computeLongitudinalData();

  const options: ChartOptions<"bar"> = {
    responsive: true,
    elements: {
      bar: {},
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: t("modes.longitudinal.date"),
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "Volume (ml)",
        },
      },
    },
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "WMH evolution",
      },
    },
  };

  return (
    <div data-testid="viewer-mode-longitudinal-graph">
      <Bar key={data.labels.join("_")} options={options} data={data} />
    </div>
  );
}

export default LongitudinalGraph;
